import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Search, Users, ChevronLeft, ChevronRight, Upload, Download } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function PatientsList() {
  const token = localStorage.getItem('vendor_token');
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
  const fileRef = useRef(null);

  const [patients, setPatients] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(0);
  const [search, setSearch] = useState('');
  const [showDialog, setShowDialog] = useState(false);
  const [editPatient, setEditPatient] = useState(null);
  const [form, setForm] = useState({ title: '', first_name: '', last_name: '', sex: '', age_years: '', age_months: '', age_days: '', mobile: '', email: '', address: '', aadhaar: '' });
  const [selected, setSelected] = useState([]);

  const fetchPatients = useCallback(async () => {
    const params = new URLSearchParams({ page, limit: 50 });
    if (search) params.append('search', search);
    const res = await fetch(`${API}/api/lis/patients?${params}`, { headers: { Authorization: `Bearer ${token}` } });
    const data = await res.json();
    setPatients(data.patients || []);
    setTotal(data.total || 0);
    setPages(data.pages || 0);
  }, [page, search, token]);

  useEffect(() => { fetchPatients(); }, [fetchPatients]);

  const openCreate = () => { setEditPatient(null); setForm({ title: '', first_name: '', last_name: '', sex: '', age_years: '', age_months: '', age_days: '', mobile: '', email: '', address: '', aadhaar: '' }); setShowDialog(true); };
  const openEdit = (p) => {
    setEditPatient(p);
    setForm({ title: p.title || '', first_name: p.first_name, last_name: p.last_name || '', sex: p.sex || '', age_years: p.age_years || '', age_months: p.age_months || '', age_days: p.age_days || '', mobile: p.mobile || '', email: p.email || '', address: p.address || '', aadhaar: p.aadhaar || '' });
    setShowDialog(true);
  };

  const save = async () => {
    const url = editPatient ? `${API}/api/lis/patients/${editPatient.id}` : `${API}/api/lis/patients`;
    const res = await fetch(url, { method: editPatient ? 'PUT' : 'POST', headers, body: JSON.stringify(form) });
    if (res.ok) { toast.success(editPatient ? 'Updated' : 'Patient created'); setShowDialog(false); fetchPatients(); }
    else toast.error('Failed');
  };

  const deletePatient = async (id) => { if (!window.confirm('Delete?')) return; await fetch(`${API}/api/lis/patients/${id}`, { method: 'DELETE', headers }); toast.success('Deleted'); fetchPatients(); };

  const toggleSelect = (id) => setSelected(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);
  const toggleAll = () => setSelected(selected.length === patients.length ? [] : patients.map(p => p.id));

  const bulkDelete = async () => {
    if (!window.confirm(`Delete ${selected.length} selected patient(s)?`)) return;
    const res = await fetch(`${API}/api/lis/patients/bulk-delete`, { method: 'POST', headers, body: JSON.stringify({ ids: selected }) });
    if (res.ok) {
      const data = await res.json();
      toast.success(`Deleted ${data.deleted} patient(s)`);
      setSelected([]);
      fetchPatients();
    } else toast.error('Bulk delete failed');
  };

  const handleCSVUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    const res = await fetch(`${API}/api/lis/patients/bulk-upload`, { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd });
    const data = await res.json();
    toast.success(`Uploaded ${data.created} patients`);
    if (data.errors?.length) toast.error(`${data.errors.length} errors`);
    fetchPatients();
    e.target.value = '';
  };

  const getAge = (p) => {
    const parts = [];
    if (p.age_years) parts.push(`${p.age_years}y`);
    if (p.age_months) parts.push(`${p.age_months}m`);
    if (p.age_days) parts.push(`${p.age_days}d`);
    return parts.join(' ') || '-';
  };

  return (
    <div data-testid="lis-patients-list">
      <div className="flex items-center justify-between mb-4">
        <div><h1 className="text-xl font-bold text-slate-800">Patients</h1><p className="text-sm text-slate-500">{total} patients</p></div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => window.open(`${API}/api/lis/business/templates/patients`, '_blank')} data-testid="download-patient-template-btn">
            <Download className="w-3.5 h-3.5 mr-1" /> Template
          </Button>
          <input type="file" ref={fileRef} accept=".csv" onChange={handleCSVUpload} className="hidden" />
          <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} data-testid="patient-csv-upload-btn">
            <Upload className="w-3.5 h-3.5 mr-1" /> CSV Upload
          </Button>
          <Button size="sm" onClick={openCreate} data-testid="add-patient-btn"><Plus className="w-3.5 h-3.5 mr-1" /> Add Patient</Button>
        </div>
      </div>

      <Card className="mb-4 border-slate-200"><CardContent className="p-3">
        <div className="relative"><Search className="absolute left-2.5 top-2.5 w-4 h-4 text-slate-400" /><Input placeholder="Search by name, mobile, ID..." value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} className="pl-9 h-9" data-testid="search-patients" /></div>
      </CardContent></Card>

      {selected.length > 0 && (
        <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center justify-between" data-testid="patients-selection-bar">
          <span className="font-medium text-blue-700 text-sm">{selected.length} patient(s) selected</span>
          <Button variant="destructive" size="sm" className="h-7 text-xs" onClick={bulkDelete} data-testid="bulk-delete-patients-btn">
            <Trash2 className="w-3 h-3 mr-1" /> Delete Selected
          </Button>
        </div>
      )}

      <Card className="border-slate-200"><div className="overflow-x-auto"><table className="w-full text-sm"><thead><tr className="border-b bg-slate-50">
        <th className="p-3 text-left w-10"><input type="checkbox" checked={selected.length === patients.length && patients.length > 0} onChange={toggleAll} data-testid="select-all-patients" /></th>
        <th className="p-3 text-left font-medium text-slate-600">ID</th><th className="p-3 text-left font-medium text-slate-600">Name</th>
        <th className="p-3 text-left font-medium text-slate-600">Sex</th><th className="p-3 text-left font-medium text-slate-600">Age</th>
        <th className="p-3 text-left font-medium text-slate-600">Mobile</th><th className="p-3 text-right font-medium text-slate-600 w-20">Actions</th>
      </tr></thead><tbody>
        {patients.map(p => (
          <tr key={p.id} className="border-b border-slate-100 hover:bg-slate-50">
            <td className="p-3"><input type="checkbox" checked={selected.includes(p.id)} onChange={() => toggleSelect(p.id)} data-testid={`select-patient-${p.id}`} /></td>
            <td className="p-3 text-xs text-slate-500">#{p.patient_id}</td>
            <td className="p-3 font-medium text-slate-800">{[p.title, p.first_name, p.last_name].filter(Boolean).join(' ')}</td>
            <td className="p-3 text-slate-500">{p.sex || '-'}</td>
            <td className="p-3 text-slate-500">{getAge(p)}</td>
            <td className="p-3 text-slate-500">{p.mobile || '-'}</td>
            <td className="p-3 text-right"><div className="flex gap-1 justify-end">
              <button onClick={() => openEdit(p)} className="p-1 text-slate-400 hover:text-blue-600"><Pencil className="w-3.5 h-3.5" /></button>
              <button onClick={() => deletePatient(p.id)} className="p-1 text-slate-400 hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>
            </div></td>
          </tr>
        ))}
        {patients.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-slate-400">No patients yet.</td></tr>}
      </tbody></table></div>
      {pages > 1 && <div className="flex items-center justify-between p-3 border-t"><span className="text-xs text-slate-500">Page {page}/{pages}</span><div className="flex gap-1"><Button variant="outline" size="sm" disabled={page<=1} onClick={()=>setPage(p=>p-1)}><ChevronLeft className="w-4 h-4" /></Button><Button variant="outline" size="sm" disabled={page>=pages} onClick={()=>setPage(p=>p+1)}><ChevronRight className="w-4 h-4" /></Button></div></div>}
      </Card>

      <Dialog open={showDialog} onOpenChange={setShowDialog}><DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{editPatient ? 'Edit Patient' : 'Add Patient'}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-3 gap-3">
            <Select value={form.title} onValueChange={v => setForm(f => ({ ...f, title: v }))}><SelectTrigger><SelectValue placeholder="Title" /></SelectTrigger><SelectContent><SelectItem value="Mr.">Mr.</SelectItem><SelectItem value="Mrs.">Mrs.</SelectItem><SelectItem value="Ms.">Ms.</SelectItem><SelectItem value="Dr.">Dr.</SelectItem></SelectContent></Select>
            <Input placeholder="First Name *" value={form.first_name} onChange={e => setForm(f => ({ ...f, first_name: e.target.value }))} data-testid="patient-fname" />
            <Input placeholder="Last Name" value={form.last_name} onChange={e => setForm(f => ({ ...f, last_name: e.target.value }))} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Select value={form.sex} onValueChange={v => setForm(f => ({ ...f, sex: v }))}><SelectTrigger><SelectValue placeholder="Sex" /></SelectTrigger><SelectContent><SelectItem value="MALE">Male</SelectItem><SelectItem value="FEMALE">Female</SelectItem></SelectContent></Select>
            <Input placeholder="Mobile" value={form.mobile} onChange={e => setForm(f => ({ ...f, mobile: e.target.value }))} />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <Input type="number" placeholder="Years" value={form.age_years} onChange={e => setForm(f => ({ ...f, age_years: e.target.value }))} />
            <Input type="number" placeholder="Months" value={form.age_months} onChange={e => setForm(f => ({ ...f, age_months: e.target.value }))} />
            <Input type="number" placeholder="Days" value={form.age_days} onChange={e => setForm(f => ({ ...f, age_days: e.target.value }))} />
          </div>
          <Input placeholder="Email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
          <Input placeholder="Address" value={form.address} onChange={e => setForm(f => ({ ...f, address: e.target.value }))} />
          <Input placeholder="Aadhaar" value={form.aadhaar} onChange={e => setForm(f => ({ ...f, aadhaar: e.target.value }))} />
        </div>
        <DialogFooter><Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button><Button onClick={save} data-testid="save-patient-btn">{editPatient ? 'Update' : 'Create'}</Button></DialogFooter>
      </DialogContent></Dialog>
    </div>
  );
}
